// Cấu hình người dùng của các danh sách (AntGrid): MỘT object JSON trong localStorage, sẵn sàng đưa về database.
//
// {
//   version: 1,
//   grids: {
//     "voucher:EXPOrder/tab:EXPOrderItems/grid:EXPOrderItem": {   // khoá = phạm vi nghiệp vụ (AntGridScope) + tab + lưới
//       scope: "voucher:EXPOrder",            // phạm vi: voucher/pos/popup/widget/setting, hoặc screen:<route pattern>
//       grid: "EXPOrderItem",
//       table: "EXPOrderItem",
//       route: "/exp/EXPOrder/:code",        // route lúc lưu gần nhất — chỉ để tra cứu, KHÔNG thuộc khoá
//       updatedAt: 1790000000,                // Unix
//       columns: { description: { width: 258 }, project_feature_name: { visible: false } },
//       sort: { field: "created_at", order: "desc" },
//       filters: { searchText, filterBySelected, filteredGroup, dateFilter, categories: { "1": [...] } },
//       categoriesExpanded: { "<lookups>": true },
//       view: { showSearchForm, showScheduler, showCardView, schedulerCurrentView, schedulerCurrentDate }
//     }
//   }
// }
//
// Loại mục (đoạn cuối của khoá):
//   grid:<bảng|settingsKey>   lưới AntGrid          — columns, columnOrder, sort, filters, categoriesExpanded, view
//   page:<bảng>               màn danh sách AntSearchPage — searchForm.conditions, view (showSearchForm, isDetailMode)
//   pivot:<tên>               AntPivotGrid          — sort, filters, view (showSearchForm, showChart)
//   lookup:<tên editor>       lưới trong ô chọn (AntDropDownGrid, đoạn đầu khoá) — columns, columnOrder, sort, popup,
//                             categoriesExpanded, view.showFilterRow (KHÔNG lưu giá trị đang lọc)
//
// Chưa đưa về BE: mọi đọc/ghi đi qua một cổng (setGridSettingsStorage) — mặc định localStorage; khi làm BE chỉ cần
// thay cổng này (hoặc dùng exportGridSettings / importGridSettings để đồng bộ), không phải sửa từng component.
// Không chứa bộ đệm đo độ rộng tự động (AntGridBestFit) — đó là số liệu tính lại được, không phải cấu hình.

import cs from './const';

export const GRID_SETTINGS_VERSION = 1;
export const getGridSettingsStorageKey = () => cs.System_Code + '-grid-settings';

let memory = null;
// Cổng đọc/ghi: { getItem(key), setItem(key, value), removeItem(key) } — mặc định localStorage.
let customStorage = null;

export const setGridSettingsStorage = (storage) => {
    customStorage = storage && typeof storage.getItem === 'function' && typeof storage.setItem === 'function' ? storage : null;
};

const getStorage = () => {
    if (customStorage) return customStorage;
    try {
        return typeof window !== 'undefined' ? window.localStorage : null;
    } catch (e) {
        return null;
    }
};

const emptyStore = () => ({ version: GRID_SETTINGS_VERSION, grids: {} });

export const readAllGridSettings = () => {
    const storage = getStorage();
    if (!storage) return memory || emptyStore();
    try {
        const parsed = JSON.parse(storage.getItem(getGridSettingsStorageKey()) || 'null');
        if (parsed && typeof parsed === 'object' && parsed.grids && typeof parsed.grids === 'object' && !Array.isArray(parsed.grids)) return parsed;
    } catch (e) {
        // hỏng dữ liệu: làm lại từ đầu
    }
    return emptyStore();
};

const writeAllGridSettings = (store) => {
    const storage = getStorage();
    if (!storage) {
        memory = store;
        return;
    }
    try {
        storage.setItem(getGridSettingsStorageKey(), JSON.stringify(store));
    } catch (e) {
        // localStorage đầy/bị chặn: chỉ mất ghi nhớ.
    }
};

// Đoạn phạm vi của khung chứa: "loại:tên[:tên]" (vd popup:batch-update:AROrder); "/" và "#" trong tên đổi thành "_".
const cleanSegmentPart = (value) =>
    String(value === undefined || value === null ? '' : value)
        .trim()
        .replace(/[#/]/g, '_');
export const buildGridScopeSegment = (kind, ...names) => {
    const parts = names.map(cleanSegmentPart).filter(Boolean);
    return parts.length ? cleanSegmentPart(kind) + ':' + parts.join(':') : null;
};

// Đoạn phạm vi của màn khi không có khung chứa khai báo: route pattern, "/" thành "." (vd screen:exp.EXPOrders).
export const buildScreenScope = (routePattern) => {
    const path = String(routePattern || '')
        .split('/')
        .filter(Boolean)
        .join('.');
    return path ? 'screen:' + path : null;
};

// Toàn bộ cấu hình (bản sao) — để đồng bộ về BE sau này.
export const exportGridSettings = () => JSON.parse(JSON.stringify(readAllGridSettings()));

// Nạp cấu hình (vd từ BE): merge = true → mục mới hơn (updatedAt) thắng từng khoá; false → thay toàn bộ.
export const importGridSettings = (store, options = {}) => {
    if (!store || typeof store !== 'object' || !store.grids || typeof store.grids !== 'object') return false;
    const incoming = store.grids;
    const next = options.merge ? readAllGridSettings() : emptyStore();
    Object.keys(incoming).forEach((key) => {
        const entry = incoming[key];
        if (!entry || typeof entry !== 'object') return;
        const current = next.grids[key];
        if (!options.merge || !current || (entry.updatedAt || 0) >= (current.updatedAt || 0)) next.grids[key] = entry;
    });
    next.version = GRID_SETTINGS_VERSION;
    writeAllGridSettings(next);
    return true;
};

// Khoá lưới: các đoạn "loại:tên" nối bằng "/" — đọc được bằng mắt, dùng làm khoá khi lưu về database.
export const buildGridSettingsKey = (...segments) =>
    segments
        .flat()
        .filter((segment) => segment !== undefined && segment !== null && String(segment) !== '')
        .map(String)
        .join('/');

export const readGridSettings = (key) => {
    const entry = readAllGridSettings().grids[key];
    return entry && typeof entry === 'object' ? entry : {};
};

const isEmptyValue = (value) =>
    value === undefined || value === null || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

// Cập nhật một lưới: updater(entry) trả entry mới (hoặc sửa tại chỗ). meta ghi route/grid/table cho dễ đọc.
export const updateGridSettings = (key, updater, meta) => {
    if (!key) return {};
    const store = readAllGridSettings();
    const current = { ...(store.grids[key] || {}) };
    const next = (typeof updater === 'function' ? updater(current) : { ...current, ...updater }) || current;
    Object.keys(next).forEach((k) => isEmptyValue(next[k]) && delete next[k]);
    const content = Object.keys(next).filter((k) => !['scope', 'route', 'grid', 'table', 'updatedAt'].includes(k));
    if (content.length === 0) delete store.grids[key];
    else {
        if (meta && typeof meta === 'object')
            Object.keys(meta).forEach((k) => {
                if (meta[k] !== undefined && meta[k] !== null && meta[k] !== '') next[k] = meta[k];
            });
        next.updatedAt = Math.floor(Date.now() / 1000);
        store.grids[key] = next;
    }
    store.version = GRID_SETTINGS_VERSION;
    writeAllGridSettings(store);
    return next;
};

// Gộp độ rộng/ẩn hiện từng cột: { field: { width, visible } }; giá trị null xoá thuộc tính đó.
export const mergeColumnSettings = (columns, patch) => {
    const next = { ...(columns || {}) };
    Object.keys(patch || {}).forEach((field) => {
        const item = { ...(next[field] || {}), ...patch[field] };
        Object.keys(item).forEach((k) => (item[k] === null || item[k] === undefined) && delete item[k]);
        if (Object.keys(item).length) next[field] = item;
        else delete next[field];
    });
    return next;
};

export const getSavedColumnWidths = (entry) => {
    const widths = {};
    Object.keys((entry && entry.columns) || {}).forEach((field) => {
        const width = entry.columns[field] && entry.columns[field].width;
        if (typeof width === 'number' && width > 20) widths[field] = width;
    });
    return widths;
};

export const getSavedColumnVisibility = (entry) => {
    const visibility = {};
    Object.keys((entry && entry.columns) || {}).forEach((field) => {
        const visible = entry.columns[field] && entry.columns[field].visible;
        if (typeof visible === 'boolean') visibility[field] = visible;
    });
    return visibility;
};

// Thứ tự cột người dùng kéo thả: columnOrder = [field...] (chỉ cột dữ liệu). Cột hệ thống (STT, chọn, thao tác) giữ
// nguyên chỗ; cột dữ liệu xếp theo thứ tự đã lưu, cột mới (chưa có trong danh sách lưu) đứng ngay sau cột đứng trước
// nó trong cấu hình gốc.
export const applyColumnOrder = (columns, order, isMovable) => {
    const list = columns instanceof Array ? columns : [];
    if (!(order instanceof Array) || order.length === 0) return list.slice();
    const movable = (col) => Boolean(col) && (isMovable ? isMovable(col) : Boolean(col.dataField));
    const moving = list.filter(movable);
    const byField = new Map(moving.map((col) => [col.dataField, col]));
    const arranged = [];
    order.forEach((field) => {
        const col = byField.get(field);
        if (col && !arranged.includes(col)) arranged.push(col);
    });
    moving.forEach((col, index) => {
        if (arranged.includes(col)) return;
        const previous = index > 0 ? moving[index - 1] : null;
        const at = previous ? arranged.indexOf(previous) : -1;
        arranged.splice(at + 1, 0, col);
    });
    let next = 0;
    return list.map((col) => (movable(col) ? arranged[next++] : col));
};

const CATEGORY_COUNT = 10;

// Trạng thái phẳng AntGrid dùng nội bộ (searchText, sortByColumn, selectedCats1..10...) ⇄ các nhóm trong JSON.
export const gridStateToSettings = (state, options = {}) => {
    const s = state || {};
    const sections = {};
    if (s.sortByColumn) sections.sort = { field: s.sortByColumn, order: s.sortType || 'asc' };
    else sections.sort = null;
    if (options.persistFilters !== false) {
        const categories = {};
        for (let i = 1; i <= CATEGORY_COUNT; i++) {
            const value = s['selectedCats' + i];
            if (value instanceof Array ? value.length > 0 : value !== undefined && value !== null && value !== '') categories[i] = value;
        }
        const filters = {};
        if (s.searchText !== undefined && s.searchText !== null && s.searchText !== '' && s.searchText !== 'undefined') filters.searchText = String(s.searchText);
        if (s.filterBySelected) filters.filterBySelected = true;
        if (s.filteredGroup) filters.filteredGroup = s.filteredGroup;
        if (s.dateFilter) filters.dateFilter = s.dateFilter;
        if (Object.keys(categories).length) filters.categories = categories;
        // Bộ lọc trên cột (filter row): { dataField: giá trị editor } — chỉ giá trị có nội dung.
        if (s.columnFilters && typeof s.columnFilters === 'object') {
            const columns = {};
            Object.keys(s.columnFilters).forEach((field) => {
                const value = s.columnFilters[field];
                if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return;
                columns[field] = value;
            });
            if (Object.keys(columns).length) filters.columns = columns;
        }
        sections.filters = Object.keys(filters).length ? filters : null;
    }
    const view = {};
    ['showSearchForm', 'showScheduler', 'showCardView', 'schedulerCurrentView', 'schedulerCurrentDate', 'showFilterRow', 'showChart'].forEach((k) => {
        if (s[k] !== undefined && s[k] !== null && s[k] !== false) view[k] = s[k];
    });
    sections.view = Object.keys(view).length ? view : null;
    return sections;
};

export const settingsToGridState = (entry) => {
    const e = entry || {};
    const state = { searchText: '', sortByColumn: null, sortType: null, filterBySelected: false, filteredGroup: null };
    if (e.sort && e.sort.field) {
        state.sortByColumn = e.sort.field;
        state.sortType = e.sort.order || 'asc';
    }
    const filters = e.filters || {};
    if (filters.searchText) state.searchText = filters.searchText;
    if (filters.filterBySelected) state.filterBySelected = true;
    if (filters.filteredGroup) state.filteredGroup = filters.filteredGroup;
    if (filters.dateFilter) state.dateFilter = filters.dateFilter;
    if (filters.columns && typeof filters.columns === 'object') state.columnFilters = { ...filters.columns };
    Object.keys(filters.categories || {}).forEach((i) => {
        state['selectedCats' + i] = filters.categories[i];
    });
    Object.assign(state, e.view || {});
    return state;
};

// Chuyển dữ liệu từ các khoá localStorage cũ (lưu rời từng loại, khoá băm) sang object chung — chạy một lần
// cho mỗi lưới, xoá phần cũ đã chuyển.
const takeLegacy = (storageKey, subKey) => {
    const storage = getStorage();
    if (!storage || !subKey) return undefined;
    try {
        const all = JSON.parse(storage.getItem(storageKey) || 'null');
        if (!all || typeof all !== 'object' || all[subKey] === undefined) return undefined;
        const value = all[subKey];
        delete all[subKey];
        if (Object.keys(all).length) storage.setItem(storageKey, JSON.stringify(all));
        else storage.removeItem(storageKey);
        return value;
    } catch (e) {
        return undefined;
    }
};

export const migrateLegacyGridSettings = (key, meta, legacy = {}) => {
    if (!key) return;
    const statesKey = cs.System_Code + '-grid-states';
    const widthsKey = cs.System_Code + '-grid-col-widths';
    const oldState = legacy.statesHash ? takeLegacy(statesKey, legacy.statesHash) : undefined;
    const oldWidths = legacy.widthsHash ? takeLegacy(widthsKey, legacy.widthsHash) : undefined;
    if (oldState === undefined && oldWidths === undefined) return;
    updateGridSettings(
        key,
        (entry) => {
            const next = { ...entry };
            if (oldState && typeof oldState === 'object') {
                const sections = gridStateToSettings(oldState, { persistFilters: legacy.persistFilters });
                Object.keys(sections).forEach((k) => {
                    if (next[k] === undefined && sections[k]) next[k] = sections[k];
                });
            }
            if (oldWidths && typeof oldWidths === 'object') {
                const patch = {};
                Object.keys(oldWidths).forEach((field) => {
                    if (typeof oldWidths[field] === 'number' && !(next.columns && next.columns[field] && next.columns[field].width))
                        patch[field] = { width: oldWidths[field] };
                });
                next.columns = mergeColumnSettings(next.columns, patch);
            }
            return next;
        },
        meta
    );
};

// Chuyển 1 mục từ khoá cũ bất kỳ (<System>-<tên>, băm theo đường dẫn) — trả giá trị cũ (đã xoá khỏi khoá cũ).
export const takeLegacyGridEntry = (legacyName, hashKey) => takeLegacy(cs.System_Code + '-' + legacyName, hashKey);

// Thu gọn/mở rộng nhóm searchCategories — khoá cũ `<System>-grid-categories-expanded`.
export const takeLegacyCategoryExpanded = (legacyKey) => takeLegacy(cs.System_Code + '-grid-categories-expanded', legacyKey);

// Bản dev: 2 lưới đang hiển thị trùng khoá (cùng bảng, cùng phạm vi) → cảnh báo để thêm settingsKey.
const mountedKeys = new Map();
export const registerGridSettingsKey = (key) => {
    if (!key) return () => {};
    const count = (mountedKeys.get(key) || 0) + 1;
    mountedKeys.set(key, count);
    if (count > 1 && typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production')
        // eslint-disable-next-line no-console
        console.warn('[AntGrid] Trùng khoá cấu hình lưới "' + key + '" — truyền settingsKey khác nhau cho các lưới cùng bảng.');
    return () => {
        const left = (mountedKeys.get(key) || 1) - 1;
        if (left > 0) mountedKeys.set(key, left);
        else mountedKeys.delete(key);
    };
};
